import { parse } from 'csv-string';
import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { innerHtml } from '../../utils/utils';

const CSVParserComponent = ({ show, onHide, onRowsReady, parser, exampleHeaders = [], compilationNotes = [] }) => {
  const [csvFile, setCsvFile] = useState<File>(null);
  const [csvError, setCsvError] = useState(false);
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (exampleHeaders.length) {
      const data = exampleHeaders.join(',');
      const blob = new Blob([data], { type: 'text/csv' });
      setObjectUrl(URL.createObjectURL(blob));
    }
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, []);

  useEffect(() => {
    if (!csvFile) return;

    const getFieldIndex = (headerFields: string[], ...fields: string[]): number => {
      for (const field of fields) {
        const index = headerFields.indexOf(field);
        if (index != -1) return index;
      }
      return null;
    };

    const reader = new FileReader();
    reader.addEventListener('load', (e) => {
      const result = (e.target as any).result as string;
      const [headerFields, ...rows] = parse(result);

      const extractSeconds = (timestamp) => {
        const [hours, minutes, seconds] = timestamp.split(':');
        return +seconds + +minutes * 60 + +hours * 3600;
      };

      const mappedRows = (() => {
        switch (parser) {
          case 'ts': {
            const indexOfIn = getFieldIndex(headerFields, 'In', 'Attacco');
            const indexOfDescription = getFieldIndex(headerFields, 'Marker Name', 'Nome marcatore');
            if (indexOfIn == null || indexOfDescription == null) {
              console.error({ indexOfIn, indexOfDescription });
              setCsvError(true);
              return;
            }
            return rows
              .map((row) => {
                return { at: extractSeconds(row[indexOfIn]), description: row[indexOfDescription] };
              })
              .filter((row) => !!row.description);
          }
          case 'xray': {
            const indexOfIn = getFieldIndex(headerFields, 'In', 'Attacco');
            const indexOfOut = getFieldIndex(headerFields, 'Out', 'Stacco');
            const indexOfDescription = getFieldIndex(headerFields, 'Marker Name', 'Nome marcatore');
            if (indexOfIn == null || indexOfOut == null || indexOfDescription == null) {
              console.error({ indexOfIn, indexOfOut, indexOfDescription });
              setCsvError(true);
              return;
            }
            return rows
              .map((row) => {
                return { from: extractSeconds(row[indexOfIn]), to: extractSeconds(row[indexOfOut]), description: row[indexOfDescription] };
              })
              .filter((row) => !!row.description);
          }
          case 'users-emails': {
            const indexOfDescription = getFieldIndex(headerFields, 'Email utente');
            if (indexOfDescription == null) {
              console.error({ indexOfDescription });
              setCsvError(true);
              return;
            }
            return rows.map((row) => row[indexOfDescription]).filter((email) => !!email);
          }
          case 'users-flow': {
            const indexOfEmail = getFieldIndex(headerFields, 'email');
            const indexOfNome = getFieldIndex(headerFields, 'nome');
            const indexOfCognome = getFieldIndex(headerFields, 'cognome');
            const indexOfPassword = getFieldIndex(headerFields, 'password');
            if (indexOfEmail == null || indexOfNome == null || indexOfCognome == null || indexOfPassword == null) {
              console.error({ indexOfEmail, indexOfNome, indexOfCognome, indexOfPassword });
              setCsvError(true);
              return;
            }
            return rows
              .map((row) => {
                return { email: row[indexOfEmail], nome: row[indexOfNome], cognome: row[indexOfCognome], password: row[indexOfPassword] };
              })
              .filter((row) => !!row.email);
          }
          case 'users-live': {
            const indexOfEmail = getFieldIndex(headerFields, 'email');
            const indexOfNome = getFieldIndex(headerFields, 'nome');
            const indexOfCognome = getFieldIndex(headerFields, 'cognome');
            const indexOfPassword = getFieldIndex(headerFields, 'password');
            const indexOfSpeaker = getFieldIndex(headerFields, 'speaker');
            if (indexOfEmail == null || indexOfNome == null || indexOfCognome == null || indexOfPassword == null || indexOfSpeaker == null) {
              console.error({ indexOfEmail, indexOfNome, indexOfCognome, indexOfPassword, indexOfSpeaker });
              setCsvError(true);
              return;
            }
            return rows
              .map((row) => {
                return {
                  email: row[indexOfEmail],
                  nome: row[indexOfNome],
                  cognome: row[indexOfCognome],
                  password: row[indexOfPassword],
                  speaker: parseInt(row[indexOfSpeaker]),
                };
              })
              .filter((row) => !!row.email);
          }
        }
      })();

      onHide();
      onRowsReady(mappedRows);
    });
    reader.readAsText(csvFile);
  }, [csvFile]);

  useEffect(() => {
    if (show) return;
    setCsvFile(null);
    setCsvError(null);
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} backdrop={'static'} keyboard={false} size={!!compilationNotes.length ? 'lg' : undefined}>
      <Modal.Header className={'align-items-baseline'}>
        <Modal.Title>Upload CSV</Modal.Title>
        {/*<span className={'text-light ml-auto'}>(Parser: {parser.toUpperCase()})</span>*/}
      </Modal.Header>
      <Modal.Body>
        {!!exampleHeaders.length && (
          <Alert variant={'info'}>
            Scarica modello compilabile:{' '}
            <strong>
              <a href={objectUrl} download={'template.csv'}>
                clicca qui
              </a>
            </strong>
          </Alert>
        )}
        {!!compilationNotes.length && (
          <Alert variant={'warning'}>
            <div>
              <strong>Note sulla compilazione:</strong>
            </div>
            <ul style={{ marginLeft: '1rem', marginBottom: 0 }}>
              {compilationNotes.map((n, i) => (
                <li key={i.toString()} {...innerHtml(n)} />
              ))}
            </ul>
          </Alert>
        )}
        <Form.Group>
          <Form.Label>
            Seleziona un file <small className={'text-muted'}>(CSV)</small>
          </Form.Label>
          <Form.File accept={'text/csv'} onChange={(e) => setCsvFile(e.target.files[0])} disabled={!!csvFile} />
        </Form.Group>
        {csvError && (
          <Alert variant={'danger'} className={'mb-0'}>
            Errore nella lettura del file CSV!
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant={'secondary'} onClick={onHide}>
          Annulla
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { CSVParserComponent as CSVParser };
